import React from 'react';
import { BarChart, DollarSign, TrendingUp, Users } from 'lucide-react';
import { useDeals } from '../../store/DealsContext';
import { formatCurrency } from '../../utils/formatters';

export default function PipelineAnalytics() {
  const { deals } = useDeals();

  const metrics = {
    totalValue: deals.reduce((sum, deal) => sum + deal.value, 0),
    avgDealSize: deals.length 
      ? deals.reduce((sum, deal) => sum + deal.value, 0) / deals.length 
      : 0,
    activeDeals: deals.filter(d => d.status !== 'completed').length,
    winRate: deals.length 
      ? (deals.filter(d => d.status === 'completed').length / deals.length) * 100 
      : 0
  };

  const stageDistribution = {
    prospect: deals.filter(d => d.status === 'prospect').length,
    due_diligence: deals.filter(d => d.status === 'due_diligence').length,
    negotiation: deals.filter(d => d.status === 'negotiation').length,
    closing: deals.filter(d => d.status === 'closing').length,
    completed: deals.filter(d => d.status === 'completed').length
  };

  return (
    <div className="space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Pipeline Value</h3>
            <DollarSign className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(metrics.totalValue)}
          </p>
          <p className="mt-1 text-sm text-gray-500">Total deal value</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Average Deal Size</h3>
            <BarChart className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(metrics.avgDealSize)}
          </p>
          <p className="mt-1 text-sm text-gray-500">Per transaction</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Active Deals</h3>
            <Users className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.activeDeals}</p>
          <p className="mt-1 text-sm text-gray-500">In progress</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Win Rate</h3>
            <TrendingUp className="w-5 h-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.winRate.toFixed(1)}%</p>
          <p className="mt-1 text-sm text-gray-500">Success rate</p>
        </div>
      </div>

      {/* Pipeline Distribution */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Pipeline Distribution</h3>
        <div className="space-y-4">
          {Object.entries(stageDistribution).map(([stage, count]) => (
            <div key={stage}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-600 capitalize">
                  {stage.replace('_', ' ')}
                </span>
                <span className="text-sm text-gray-500">
                  {count} deals ({((count / deals.length) * 100).toFixed(1)}%)
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-blue-600 rounded-full"
                  style={{ width: `${(count / deals.length) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}